import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { API_BASE_URL } from '../lib/constans';

const ANIMATION_VARIANTS = {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.98 },
};

export default function DecksPlan({ decksData = [], yacht }) {

    const formattedDecks = useMemo(() => {
        if (!Array.isArray(decksData) || decksData.length === 0) return [];
        return decksData;
    }, [decksData]);

    const [selectedDeck, setSelectedDeck] = useState(() => {
        const defaultDeck = decksData.find(d => d.type === 'Main Deck');
        return defaultDeck || decksData[0] || null;
    });

    if (formattedDecks.length === 0 || !selectedDeck) {
        return (
            <div className="w-full p-8 text-center text-gray-400 italic">
                No se encontraron planos de cubiertas disponibles.
            </div>
        );
    }

    return (
        <section className="w-full py-6 bg-p-beige shadow-sm text-p-blue font-sans">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold tracking-wider uppercase text-p-blue inline-block border-b-4 border-p-orange pb-1">
                    Deck Plan
                </h2>
            </div>
            <div className="relative min-h-[650px] w-full flex items-center justify-center overflow-hidden">

                <div className="absolute top-0 font-semibold left-0 md:pl-30 md:w-lg bg-black/20 backdrop-blur-sm text-white p-4 rounded-r-xl z-10 shadow-lg">
                    <p>Length: {yacht.length} | Beam: {yacht.beam}12.30 m</p>
                    <p>Draft: {yacht.draft} | Cruising Speed: {yacht.speed}</p>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedDeck.type}
                        variants={ANIMATION_VARIANTS}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="relative w-full max-w-4xl h-[400px] md:mt-10"
                    >
                        <img
                            src={API_BASE_URL + selectedDeck.image}
                            alt={`Plano de la cubierta ${selectedDeck.type}`}
                            fill
                            priority
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                            className="object-contain"
                        />
                    </motion.div>
                </AnimatePresence>
            </div>

            <hr className="mb-3 border-gray-200/60" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 md:px-20">
                <nav className="flex flex-wrap gap-2 md:gap-4 items-center justify-center" aria-label="Deck selection">
                    {formattedDecks.map((deck) => {
                        const isSelected = selectedDeck.type === deck.type;
                        return (
                            <DeckButton
                                key={deck.type}
                                label={deck.type}
                                isActive={isSelected}
                                onClick={() => setSelectedDeck(deck)}
                            />
                        );
                    })}
                </nav>
                <Tooltip title="Ver especificaciones técnicas del yate" placement="topRight">
                    <a
                        href={API_BASE_URL + yacht.file}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="inline-flex items-center gap-2 text-sm md:text-base  hover:text-p-orange/70 transition-colors"
                    >
                        Technical Information <DownloadOutlined className="text-base" />
                    </a>
                </Tooltip>
            </div>

        </section>
    );
}

const DeckButton = React.memo(({ label, isActive, onClick }) => {
    return (
        <button
            aria-label="Search"
            onClick={onClick}
            type="button"
            className={`cursor-pointer px-5 py-2 text-sm md:text-base font-bold rounded-md transition-all duration-200 ${isActive
                ? 'bg-p-blue text-white shadow-sm scale-102'
                : 'text-orange-500 hover:text-orange-600 hover:bg-orange-50/60'
                }`}
        >
            {label}
        </button>
    );
});

DeckButton.displayName = 'DeckButton';